import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface RateLimitInfo {
  attemptsRemaining?: number;
  rateLimited?: boolean;
  retryAfterMinutes?: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null; rateLimitInfo?: RateLimitInfo }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null; rateLimitInfo?: RateLimitInfo }>;
  signOut: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<{ error: Error | null; rateLimited?: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'app_session_id';
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const appSessionIdRef = useRef<string | null>(null);

  // Session heartbeat to track activity
  const sendHeartbeat = async () => {
    const sessionId = appSessionIdRef.current || sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId || !user) return;

    try {
      const { data, error } = await supabase.functions.invoke('rate-limited-auth', {
        body: { action: 'heartbeat', sessionId }
      });

      if (error || data?.sessionExpired) {
        console.log('Session expired, logging out');
        await signOut();
      }
    } catch (err) {
      console.error('Heartbeat error:', err);
    }
  };

  // Start heartbeat when user logs in
  const startHeartbeat = (sessionId: string, skipInitial = false) => {
    appSessionIdRef.current = sessionId;
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    
    // Clear any existing interval
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    // Send initial heartbeat only if not skipped (skip on fresh login to avoid race condition)
    if (!skipInitial) {
      sendHeartbeat();
    }
    
    // Set up periodic heartbeat
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  };

  // Stop heartbeat on logout
  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    appSessionIdRef.current = null;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // If user logs out externally, stop heartbeat
        if (!session) {
          stopHeartbeat();
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Resume heartbeat if session exists
      const existingSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (session && existingSessionId) {
        startHeartbeat(existingSessionId);
      }
    });

    return () => {
      subscription.unsubscribe();
      stopHeartbeat();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: Error | null; rateLimitInfo?: RateLimitInfo }> => {
    try {
      const { data, error } = await supabase.functions.invoke('rate-limited-auth', {
        body: { email, password, action: 'login' }
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      if (data?.error) {
        return { 
          error: new Error(data.error),
          rateLimitInfo: {
            attemptsRemaining: data.attemptsRemaining,
            rateLimited: data.rateLimited,
            retryAfterMinutes: data.retryAfterMinutes
          }
        };
      }

      // Set the auth session locally FIRST (before heartbeat)
      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });
      }

      // Start session tracking AFTER session is set
      if (data?.appSessionId) {
        startHeartbeat(data.appSessionId, true); // Skip immediate heartbeat
      }

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Authentication failed') };
    }
  };

  const signUp = async (email: string, password: string): Promise<{ error: Error | null; rateLimitInfo?: RateLimitInfo }> => {
    try {
      const { data, error } = await supabase.functions.invoke('rate-limited-auth', {
        body: { email, password, action: 'signup' }
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      if (data?.error) {
        return { 
          error: new Error(data.error),
          rateLimitInfo: {
            attemptsRemaining: data.attemptsRemaining,
            rateLimited: data.rateLimited,
            retryAfterMinutes: data.retryAfterMinutes
          }
        };
      }

      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token
        });
      }

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Registration failed') };
    }
  };

  const requestPasswordReset = async (email: string): Promise<{ error: Error | null; rateLimited?: boolean }> => {
    try {
      const { data, error } = await supabase.functions.invoke('rate-limited-auth', {
        body: { email, action: 'password-reset' }
      });

      if (error) {
        return { error: new Error(error.message) };
      }

      if (data?.error) {
        return { error: new Error(data.error), rateLimited: data.rateLimited };
      }

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Password reset failed') };
    }
  };

  const signOut = async () => {
    const sessionId = appSessionIdRef.current || sessionStorage.getItem(SESSION_STORAGE_KEY);
    
    // Notify server to remove session
    if (sessionId) {
      try {
        await supabase.functions.invoke('rate-limited-auth', {
          body: { action: 'logout', sessionId }
        });
      } catch (err) {
        console.error('Logout notification error:', err);
      }
    }
    
    stopHeartbeat();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut, requestPasswordReset }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
